/* Compile-Jobs im Editor: starten, beobachten (Abfrage im Sekundentakt, solange der Job läuft), abbrechen, erneut starten.
 * Der Job gehört dem Server: ein Neuladen der Seite hängt sich per resume() wieder an. Ohne DOM, Zeitgeber werden injiziert. */
import type { BackendClient, CompileJob } from './backend.ts';

export const PROFILES = ['fast', 'normal', 'final'] as const;
export type ProfileName = typeof PROFILES[number];
/** Engine für bsp, vis und rad (csg gibt es nur von SDHLT): SDHLT-Original oder Rust-Ports */
/** Obergrenze der Threadzahl (wie im Backend, jobs.MaxThreads) */
export const MAX_THREADS = 32;
/** Eingabe → Threadzahl 1..MAX_THREADS; leer oder ungültig = undefined (Vorgabe des Workers) */
export function parseThreads(v: string): number | undefined {
  const n = Math.floor(Number(v));
  return v.trim() !== '' && Number.isFinite(n) && n >= 1 ? Math.min(n, MAX_THREADS) : undefined;
}
export const ENGINES = ['sdhlt', 'rust'] as const;
export type EngineName = typeof ENGINES[number];
export const engineOf = (j: { engine?: string } | null): EngineName => (j && j.engine === 'rust' ? 'rust' : 'sdhlt');

export interface CompileSnapshot {
  version: number;
  job: CompileJob | null;
  log: string;
  /** letzter Fehler bei der Abfrage oder beim Start (null = keiner) */
  error: string | null;
  /** Anfrage läuft (Start, Abbruch) */
  busy: boolean;
}

export interface CompileDeps {
  client: () => BackendClient | null;
  t: (key: string, params?: Record<string, unknown>) => string;
  say: (msg: string) => void;
  /** wird nach jedem beendeten Job aufgerufen (für Hinweise im Editor) */
  finished?: (job: CompileJob) => void;
  interval?: number;
  setTimer?: (f: () => void, ms: number) => unknown;
  clearTimer?: (t: unknown) => void;
}

/** Meldung beim Ende je Zustand */
export const DONE_KEYS: Record<string, string> = {
  ok: 'compile.done_ok', failed: 'compile.done_failed', cancelled: 'compile.done_cancelled', timeout: 'compile.done_timeout', interrupted: 'compile.done_interrupted'
};

export const isActive = (j: CompileJob | null): boolean => !!j && (j.state === 'queued' || j.state === 'running');
export const canRetry = (j: CompileJob | null): boolean => !!j && !isActive(j) && j.state !== 'ok';
export const hasFile = (j: CompileJob | null, name: string): boolean => !!j && !!j.artifacts && j.artifacts.some((a) => a.name === name);

/** Dauer in Sekunden: bei laufenden Jobs bis jetzt, sonst bis zum Ende (0, wenn noch nicht gestartet) */
export function elapsedSec(j: CompileJob, now: number): number {
  if (!j.started) return 0;
  return Math.max(0, Math.round(((j.ended || now) - j.started) / 1000));
}
export function fmtDuration(sec: number): string {
  const m = Math.floor(sec / 60), s = sec % 60;
  return m ? m + ':' + String(s).padStart(2, '0') + ' min' : s + ' s';
}

export class CompileLink {
  private deps: CompileDeps;
  private job: CompileJob | null = null;
  private log = '';
  private logBytes = -1;   // logBytes des zuletzt geholten Logs
  private error: string | null = null;
  private busy = false;
  private version = 0;
  private snap: CompileSnapshot;
  private listeners = new Set<() => void>();
  private timer: unknown = null;
  private polling = false;
  private gen = 0;   // erhöht bei jedem Wechsel des beobachteten Jobs, damit späte Antworten verworfen werden

  constructor(deps: CompileDeps) { this.deps = deps; this.snap = this.build(); }

  private build(): CompileSnapshot { return { version: this.version, job: this.job, log: this.log, error: this.error, busy: this.busy }; }
  private emit(): void { this.version++; this.snap = this.build(); this.listeners.forEach((f) => f()); }
  subscribe = (fn: () => void): (() => void) => { this.listeners.add(fn); return () => { this.listeners.delete(fn); }; };
  getSnapshot = (): CompileSnapshot => this.snap;

  private setT = (f: () => void, ms: number): unknown => (this.deps.setTimer ? this.deps.setTimer(f, ms) : setTimeout(f, ms));
  private clearT = (t: unknown): void => { if (this.deps.clearTimer) this.deps.clearTimer(t); else clearTimeout(t as ReturnType<typeof setTimeout>); };
  private stop(): void { if (this.timer) { this.clearT(this.timer); this.timer = null; } }
  private client(): BackendClient { const c = this.deps.client(); if (!c) throw new Error('kein Backend'); return c; }

  /** Beobachtung beenden (der Job läuft auf dem Server weiter) */
  detach(): void { this.stop(); this.gen++; this.job = null; this.log = ''; this.logBytes = -1; this.error = null; this.emit(); }

  /** Job der Karte starten (idempotent: ein gleicher laufender Job wird einfach beobachtet) */
  async start(mapId: string, profile: ProfileName, engine: EngineName = 'sdhlt', threads?: number): Promise<boolean> {
    this.busy = true; this.error = null; this.emit();
    try {
      const job = await this.client().compileStart(mapId, profile, engine, threads);
      this.attach(job);
      this.deps.say(this.deps.t('compile.started', { name: job.mapName }));
      return true;
    } catch (e) {
      this.error = (e as { status?: number }).status === 429 ? this.deps.t('compile.too_many') : this.deps.t('compile.start_failed', { msg: (e as Error).message });
      return false;
    } finally { this.busy = false; this.emit(); }
  }

  /** Einen vorhandenen Job beobachten */
  attach(job: CompileJob): void {
    this.stop(); this.gen++;
    this.job = job; this.log = ''; this.logBytes = -1; this.error = null;
    this.emit();
    void this.poll(this.gen, true);
  }

  /** Nach dem Laden der Seite: den neuesten aktiven Job des Benutzers wieder beobachten */
  async resume(): Promise<CompileJob | null> {
    try {
      const list = await this.client().compileList();
      const j = list.find((x) => isActive(x)) || null;   // Liste: neueste zuerst
      if (j) this.attach(j);
      return j;
    } catch (e) { return null; }
  }

  private async poll(gen: number, first = false): Promise<void> {
    if (gen !== this.gen || this.polling) return;
    this.polling = true;
    const cur = this.job;
    try {
      if (!cur) return;
      const c = this.client();
      let { job } = await c.compileGet(cur.id);
      if (gen !== this.gen) return;
      if (first || job.logBytes !== this.logBytes || !isActive(job)) {
        const full = await c.compileGet(cur.id, true);
        if (gen !== this.gen) return;
        job = full.job; this.log = full.log || ''; this.logBytes = job.logBytes;
      }
      const wasActive = isActive(this.job);
      this.job = job; this.error = null; this.emit();
      if (wasActive && !isActive(job)) { this.deps.say(this.deps.t(DONE_KEYS[job.state], { name: job.mapName })); this.deps.finished?.(job); }
    } catch (e) {
      if (gen !== this.gen) return;
      const st = (e as { status?: number }).status;
      if (st === 404 || st === 401) { this.stop(); this.error = this.deps.t(st === 401 ? 'compile.signed_out' : 'compile.gone'); this.emit(); return; }
      this.error = this.deps.t('compile.poll_failed', { msg: (e as Error).message }); this.emit();
    } finally { this.polling = false; }
    if (gen === this.gen && isActive(this.job)) this.timer = this.setT(() => { this.timer = null; void this.poll(gen); }, this.deps.interval ?? 1000);
  }

  /** Abbrechen (wartend: sofort; laufend: der Worker beendet die Prozesse innerhalb weniger Sekunden) */
  async cancel(): Promise<void> {
    if (!this.job || !isActive(this.job)) return;
    this.busy = true; this.emit();
    try {
      const j = await this.client().compileCancel(this.job.id);
      this.job = j; this.error = null;
      if (!isActive(j)) { this.stop(); this.deps.say(this.deps.t(DONE_KEYS[j.state], { name: j.mapName })); this.deps.finished?.(j); }
    } catch (e) { this.error = this.deps.t('compile.cancel_failed', { msg: (e as Error).message }); }
    finally { this.busy = false; this.emit(); }
  }

  /** Mit gleicher Karte, gleichem Profil, gleicher Engine und gleicher Threadzahl erneut starten */
  async retry(): Promise<boolean> {
    const j = this.job;
    if (!j || !canRetry(j)) return false;
    return this.start(j.map, j.profile as ProfileName, engineOf(j), j.threads || undefined);
  }

  /** Ergebnisdatei holen (map.bsp, map.pts …) */
  async file(name: string): Promise<ArrayBuffer> {
    if (!this.job) throw new Error('kein Job');
    return this.client().compileFile(this.job.id, name);
  }
}
